import { Checkbox, DatePicker, Form, Input, InputNumber, Select } from "antd";
import { useState } from "react";
import useGlobalStore from "../../../../../../store/store";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../../store/adminData/adminData";
import usePackages from "../../../../../../hooks/usePackages";
import { countries } from "countries-list";
import dayjs from "dayjs";
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  writeBatch,
  query,
  getDocs,
  where,
} from "firebase/firestore";
import { db } from "../../../../../../services/firebaseService/connection";
import { toast } from "react-toastify";
import { fetchMembers } from "../../../../../../services/api/members";
import {
  fetchFormLayout,
  fetchPackages,
} from "../../../../../../services/api/packages";

const useAddMemberForm = (handleAddMemberFormModal) => {
  const [form] = Form.useForm();
  const [selectedMonth, setSelectedMonth] = useState(null);
  const { formLayout } = useGlobalStore((state) => state.formLayout);
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const [submitLoading, setSubmitLoading] = useState(false);

  const handleMemberRefresh = () => {
    fetchMembers(groupData?.id);
    fetchPackages(groupData?.id);
    fetchFormLayout(groupData?.id);
  };

  const verifyTwoWayGroupLink = async (
    linkedGroupId,
    currentGroupId,
    isLinkedGroup
  ) => {
    if (isLinkedGroup) {
      const linkedGroup = await getDoc(doc(db, "group", linkedGroupId));
      if (linkedGroup.exists()) {
        const isValid = linkedGroup
          .data()
          ?.linkedGroupId?.includes(currentGroupId);
        return {
          valid: isValid,
          linkedGroupName: linkedGroup.data()?.groupName,
        };
      }
    }
    return { valid: false, linkedGroupName: "" };
  };

  const addMemberToGroup = async (
    groupId,
    memberData,
    isLinkedGroup = false
  ) => {
    const docRef = doc(db, "invite_emails", groupId);
    const docSnapshot = await getDoc(docRef);

    if (!docSnapshot.exists()) {
      if (!isLinkedGroup) {
        throw new Error("Group invite_emails document does not exist");
      }
      return { alreadyExists: false };
    }

    const inviteEmailsData = docSnapshot.data();
    const emails = inviteEmailsData.emails || [];
    const emailIndex = emails.indexOf(memberData.email);

    const membersRef = collection(db, "userGroup", groupId, "members");
    const q = query(membersRef, where("email", "==", memberData.email));
    const querySnapshot = await getDocs(q);

    // Check if member with this email already exists in userGroup
    if (!querySnapshot.empty) {
      return { alreadyExists: true, reason: "member" };
    }

    // Check if member with this email already exists in invite_emails
    if (emailIndex !== -1) {
      return { alreadyExists: true, reason: "invited" };
    }

    const batch = writeBatch(db);

    // Add email to the emails array
    batch.update(docRef, {
      emails: arrayUnion(memberData.email),
    });

    // Create new user document
    const userDetailsRef = collection(
      db,
      "invite_emails",
      groupId,
      "userDetails"
    );
    const newUserDoc = doc(userDetailsRef);

    const safeFinalData = Object.entries(memberData).reduce(
      (acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          acc[key] = value;
        }
        return acc;
      },
      {}
    );

    batch.set(newUserDoc, safeFinalData);
    await batch.commit();

    return { alreadyExists: false };
  };

  const { packagesBySeason } = usePackages(groupData?.id);

  const countryNames = Object.values(countries).map((country) => ({
    name: country.name,
  }));

  const years = Array.from({ length: 20 }, (_, index) => ({
    year: `${dayjs().year() - index}`,
  }));

  const userRoles = [
    "admin",
    "member",
    "ticket-scanner",
    "capo",
    "leader",
    "fan",
  ];

  const Months = {
    Jan: 31,
    Feb: 29,
    Mar: 31,
    Apr: 30,
    May: 31,
    Jun: 30,
    Jul: 31,
    Aug: 31,
    Sep: 30,
    Oct: 31,
    Nov: 30,
    Dec: 31,
  };

  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);
      const values = await form.validateFields();

      // Sanitize and validate the data
      const sanitizedValues = Object.entries(values).reduce(
        (acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            // Handle nested objects
            if (typeof value === "object") {
              // Remove empty objects and objects with only undefined/null values
              const sanitizedObj = Object.entries(value).reduce(
                (objAcc, [k, v]) => {
                  if (v !== undefined && v !== null) {
                    objAcc[k] = v;
                  }
                  return objAcc;
                },
                {}
              );

              // Only add the object if it has properties
              if (Object.keys(sanitizedObj).length > 0) {
                acc[key] = sanitizedObj;
              }
            } else {
              acc[key] = value;
            }
          }
          return acc;
        },
        {}
      );

      // Ensure required fields are present
      if (!sanitizedValues.email || !sanitizedValues.fullName) {
        throw new Error("Required fields are missing");
      }

      let finalData = { ...sanitizedValues };

      finalData = {
        ...finalData,
        email: finalData?.email?.toLowerCase(),
      };

      // Handle package data safely
      if (sanitizedValues.package) {
        try {
          const packageDetails = sanitizedValues.package.split("|");
          if (packageDetails.length === 2) {
            const [packageId, packageSeason] = packageDetails;
            if (packageId && packageSeason) {
              finalData = {
                ...finalData,
                package: packageId,
                packageSeason: packageSeason,
              };
            }
          }
        } catch (error) {
          console.error("Error processing package details:", error);
        }
      }

      // Add member to primary group
      const primaryResult = await addMemberToGroup(
        groupData.id,
        finalData,
        false
      );

      if (primaryResult.alreadyExists) {
        if (primaryResult.reason === "member") {
          toast.error("A member with this email already exists in the group");
        } else if (primaryResult.reason === "invited") {
          toast.error("A member with this email has already been invited!");
        }
        return;
      }

      // Check for linked group and add member to it as well
      let linkedGroupInfo = { valid: false, linkedGroupName: "" };
      if (
        groupData?.linkedGroupId &&
        groupData?.linkedGroupId?.length > 0 &&
        groupData.id
      ) {
        linkedGroupInfo = await verifyTwoWayGroupLink(
          groupData?.linkedGroupId[0],
          groupData.id,
          true
        );
        if (linkedGroupInfo.valid) {
          try {
            await addMemberToGroup(
              groupData?.linkedGroupId[0],
              finalData,
              true
            );
          } catch (error) {
            console.error("Error adding member to linked group:", error);
            // Continue even if linked group fails
          }
        }
      }

      // Success notification with group names
      const groupNames = linkedGroupInfo.valid
        ? `${groupData?.groupName} and ${linkedGroupInfo.linkedGroupName}`
        : groupData?.groupName;

      form.resetFields();
      handleAddMemberFormModal(false);
      toast.success(`Member added successfully to ${groupNames}!`);
      handleMemberRefresh();
    } catch (error) {
      console.error("Form submission error:", error);
      if (error.errorFields) {
        const firstErrorField = error.errorFields?.[0]?.name?.[0];
        if (firstErrorField) {
          form.scrollToField(firstErrorField, {
            behavior: "smooth",
            block: "center",
          });
        }
      } else {
        toast.error("An error occurred. Please try again.");
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    handleAddMemberFormModal(false);
  };

  const getCustomField = (
    { fieldType, fieldTitle, fieldHelp, dropdownValues = [] },
    key,
    form
  ) => {
    const baseProps = {
      placeholder: fieldHelp || `Enter ${fieldTitle?.toLowerCase()}`,
      style: { width: "100%" },
    };

    const renderField = () => {
      switch (fieldType) {
        case "text":
          return <Input {...baseProps} />;

        case "number":
          return <InputNumber {...baseProps} />;

        case "checkbox":
          return (
            <div style={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                defaultChecked={false}
                onChange={(e) => {
                  // Set the form field value explicitly to false when unchecked
                  form.setFieldValue(key, e.target.checked);
                }}
              />
            </div>
          );

        case "dropdown":
          return (
            <Select {...baseProps}>
              {dropdownValues.map((value) => (
                <Select.Option key={value} value={value}>
                  {value}
                </Select.Option>
              ))}
            </Select>
          );

        case "date":
          return <DatePicker {...baseProps} format="YYYY-MM-DD" />;

        case "textarea":
          return <Input.TextArea {...baseProps} rows={4} />;

        default:
          return <Input {...baseProps} />;
      }
    };

    return (
      <Form.Item
        label={<span className="form-label">{fieldTitle}</span>}
        name={key}
      >
        {renderField()}
      </Form.Item>
    );
  };

  return {
    form,
    years,
    Months,
    userRoles,
    countryNames,
    selectedMonth,
    submitLoading,
    packagesBySeason,
    formLayout: formLayout?.[groupData?.id],
    handleSubmit,
    handleCancel,
    getCustomField,
    setSelectedMonth,
  };
};

export default useAddMemberForm;
